import React, { useState, useEffect } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Search, Loader2, Check, FolderInput } from "lucide-react";
import { docsApi, workspacesApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

// Move an existing library source INTO this workspace.
export const AddSourceDialog = ({ wsId, onAdded, trigger }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [docs, setDocs] = useState(null);
    const [q, setQ] = useState("");
    const [addingId, setAddingId] = useState(null);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try { setDocs(await docsApi.list()); } catch (e) { toast.error(t("library.loadFailed")); }
        })();
    }, [open, t]);

    const add = async (doc) => {
        setAddingId(doc.id);
        try {
            await workspacesApi.addSource(wsId, doc.id);
            setDocs((d) => d.map((x) => (x.id === doc.id ? { ...x, workspace_id: wsId } : x)));
            toast.success(t("workspace.addSource.added", { title: doc.title }));
            onAdded?.();
        } catch (e) {
            toast.error(t("workspace.addSource.failed"));
        } finally { setAddingId(null); }
    };

    const list = (docs || []).filter((d) => !q.trim() || (d.title || "").toLowerCase().includes(q.toLowerCase()));

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || <Button data-testid="ws-add-source-button" variant="secondary" className="gap-2 bg-surface-3/60"><FolderInput className="h-4 w-4" /> {t("workspace.addSource.button")}</Button>}
            </DialogTrigger>
            <DialogContent className="border-border bg-surface-1 sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-sans-display text-t1">{t("workspace.addSource.title")}</DialogTitle>
                    <DialogDescription className="sr-only">{t("workspace.sourcesEmptyBody")}</DialogDescription>
                </DialogHeader>
                <div className="mt-2">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-t4" />
                        <Input data-testid="ws-add-source-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("workspace.addSource.search")} className="h-9 bg-surface-2 pl-8 text-sm" />
                    </div>
                    <div className="mt-3 max-h-[50vh] space-y-1.5 overflow-y-auto">
                        {docs === null ? (
                            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--primary))]" /></div>
                        ) : list.length === 0 ? (
                            <p className="py-10 text-center text-sm text-t3">{t("workspace.addSource.none")}</p>
                        ) : list.map((d) => {
                            const here = d.workspace_id === wsId;
                            return (
                                <div key={d.id} data-testid={`ws-add-row-${d.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-surface-2/50 p-2.5">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-3 text-[hsl(var(--primary))]"><FileText className="h-4 w-4" /></div>
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm font-medium text-t1">{d.title}</div>
                                        <div className="mono-label text-t4">{(d.source_type || "").toUpperCase()} · {d.reading_minutes || 1} min</div>
                                    </div>
                                    {here ? (
                                        <span className="flex items-center gap-1 text-xs text-[hsl(152,52%,46%)]"><Check className="h-3.5 w-3.5" /> {t("workspace.addSource.inWorkspace")}</span>
                                    ) : (
                                        <Button data-testid={`ws-add-btn-${d.id}`} size="sm" variant="secondary" className="gap-1.5 bg-surface-3/60" disabled={addingId === d.id || d.status !== "ready"} onClick={() => add(d)}>
                                            {addingId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderInput className="h-3.5 w-3.5" />} {t("workspace.addSource.add")}
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
